import React from 'react';
import './App.css';
import { AppBar, Button, Collapse, Container, fade, InputBase, List, ListItem, ListItemIcon, ListItemText, makeStyles, Popover, Toolbar, Typography } from '@material-ui/core';
import { orange } from '@material-ui/core/colors';
import ItemDatabase, { DatabaseValue, SearchResult } from './ItemDatabase';
import Fuse from 'fuse.js';
import SearchIcon from '@material-ui/icons/Search';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ExpandLess from '@material-ui/icons/ExpandLess';
import Help from './Help';

const MAX_SEARCH_RESULTS = 20;
const EXTENSION_STORE_LINK = "https://chrome.google.com/webstore/detail/%EB%A9%94%EC%9D%B4%ED%94%8C%EC%8A%A4%ED%86%A0%EB%A6%AC-%EC%9D%B8%EB%B2%A4%ED%86%A0%EB%A6%AC-%EA%B2%80%EC%83%89-beta/bnnkkbefmdlhlmifffehooifdmecnikh?hl=ko";

const useStyles = makeStyles((theme) => ({
  main: {
    background: '#F8F8F8',
  },
  appbar: {
    backgroundColor: orange[600],
  },
  title: {
    fontWeight: 'bold',
    paddingTop: '0.7em',
    paddingBottom: '0.7em',
    paddingLeft: '1em',
    paddingRight: '1em',
  },
  search: {
    display: 'inline-flex',
    flexGrow: 1,
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: fade('#FFFFFF', 0.15),
    margin: '0.5em 1em',
    padding: '0.5em',
  },
  searchIcon: {
    dislay: 'flex',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 0.2em',
    pointerEvents: 'none',
  },
  inputRoot: {
    width: '100%',
  },
  inputInput: {
    padding: '0.1em',
  },
  extensionLink: {
    transitionDuration: "0.2s",
    marginRight: 0,
  },
  fileDropdown: {
    display: 'flex',
    border: '0.15rem dashed',
    borderRadius: theme.shape.borderRadius,
    margin: '2em',
    padding: '1em',
    minHeight: '5rem',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileDropdownDescription: {
    fontSize: '1rem',
  },
  fileDropdownButton: {
    marginBottom: '0',
  },
  fileDropdownOverlay: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: 'calc(100% - 6em)',
    height: 'calc(100% - 6em)',
    margin: '2em',
    padding: '1em',
    flexGrow: 1,
    background: fade('#FF0000', 0.2),
  },
  searchList: {
    flexGrow: 1,
  }
}));

function App() {
  const classes = useStyles();
  const [itemDatabase, setItemDatabase] = React.useState<ItemDatabase | undefined>(undefined);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [fuseResults, setFuseResults] = React.useState<SearchResult[]>([]);
  const [dragAreaHighlight, setDragAreaHighlight] = React.useState(0);
  const [showHelp, setShowHelp] = React.useState(true);
  const databaseLoadCallback = async (file: File) => {
    try {
      const json = JSON.parse(await file.text());

      if (!('version' in json && 'when' in json && 'data' in json && 'inventoryMap' in json.data && 'storageMap' in json.data && 'nameToID' in json.data)) {
        return;
      }

      const db: ItemDatabase = {
        version: json.version,
        when: json.when,
        data: json.data,
        nameFuse: new Fuse(Object.entries(json.data.nameToID).flatMap(([k, v]) => (v as any[]).map(elem => ({ name: k, id: elem }))), { keys: ["name"] }),
      };

      setItemDatabase(db);
      setAnchorEl(null);
    } catch (err) {
      console.error(err);
    }
  };
  const fileDropCallback = (ev: React.DragEvent<HTMLDivElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
    const file =
      (() => {
        if (ev.dataTransfer.items) {
          // Use DataTransferItemList interface to access the file(s)
          for (let i = 0; i < ev.dataTransfer.items.length; i++) {
            // If dropped items aren't files, reject them
            if (ev.dataTransfer.items[i].kind === 'file') {
              return ev.dataTransfer.items[i].getAsFile();
            }
          }
        } else {
          // Use DataTransfer interface to access the file(s)
          for (let i = 0; i < ev.dataTransfer.files.length; i++) {
            return ev.dataTransfer.files[i];
          }
        }
        return null;
      })();
    if (file === null) { return; }
    databaseLoadCallback(file);
  };
  const filePickCallback = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.currentTarget.files?.[0];
    if (file) {
      databaseLoadCallback(file);
    }
  }
  const searchInputCallback = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
    if (ev.target.value === null) return;
    if (ev.target.value.trim().length === 0) {
      setShowHelp(true); return;
    }
    setShowHelp(false);
    setFuseResults(itemDatabase
      ?.nameFuse
      .search(ev.target.value)
      ?.slice(0, MAX_SEARCH_RESULTS)
      ?.map(r => {
        console.log(r.item.name, r.item.id);
        let values = [...((itemDatabase.data.inventoryMap[r.item.id] ?? []) as DatabaseValue<string>[])];
        const storageItem = itemDatabase.data.storageMap[r.item.id];
        if (storageItem) {
          values.push({
            ...storageItem,
            iconURL: "",
            owner: "창고",
          });
        }
        return {
          id: r.item.id,
          name: r.item.name,
          values: values,
          totalItemCount: values.map(v => v.count).reduce((a, b) => a + b, 0),
        };
      }) ?? []);
  }, [itemDatabase]);

  return (
    <div className="app">
      <AppBar position="sticky" className={classes.appbar}>
        <Toolbar>
          <Typography variant="h6" className={classes.title}>메이플 창고 &amp; 인벤토리 검색</Typography>
          {
            itemDatabase !== undefined ? <>
              <div className={classes.search}>
                <div className={classes.searchIcon}>
                  <SearchIcon />
                </div>
                <InputBase type="search" placeholder="아이템 이름 검색..." classes={{ root: classes.inputRoot, input: classes.inputInput }} onChange={searchInputCallback}></InputBase>
              </div>
            </> : <>
              <Button onClick={(ev) => setAnchorEl(ev.currentTarget)}>아이템 목록 로드</Button>
              <Popover id="file-dropdown-popover"
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center"
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }} onClose={() => setAnchorEl(null)}>
                <div className={classes.fileDropdown}
                  onDrop={fileDropCallback}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDragEnter={() => setDragAreaHighlight(dragAreaHighlight + 1)}
                  onDragLeave={() => setDragAreaHighlight(dragAreaHighlight - 1)}
                >
                  <Typography align={'center'} variant="h6" className={classes.fileDropdownDescription}>
                    이 영역에 .json 파일을 끌어놓거나 아래의 버튼을 클릭하세요.
                  </Typography>
                  <Button variant="contained" component="label" className={classes.fileDropdownButton}>
                    파일 선택
                    <input type="file" hidden onChange={filePickCallback} />
                  </Button>
                  {dragAreaHighlight > 0 ? <div className={classes.fileDropdownOverlay} /> : <></>}
                </div>
              </Popover>
            </>
          }
          <Button className={classes.extensionLink} href={EXTENSION_STORE_LINK} target="_blank" rel="noreferrer noopener">크롬 확장 프로그램 다운로드</Button>
        </Toolbar>
      </AppBar>
      {
        showHelp ? (
          <Container maxWidth="xl" style={{ padding: '1em' }}>
            <Help />
          </Container>
        ) : (
          <Container maxWidth="md">
            <List className={classes.searchList}>
              {fuseResults.map(e => <SearchListItem item={e} />)}
            </List>
          </Container>
        )
      }
    </div >
  );
}

function SearchListItem(props: { item: SearchResult }) {
  const [expand, setExpand] = React.useState(false);
  return (<>
    <ListItem key={props.item.id} button onClick={() => setExpand(!expand)}>
      <ListItemIcon>
        <img src={`https://avatar.maplestory.nexon.com/ItemIcon/${props.item.id}.png`} alt={props.item.name} />
      </ListItemIcon>
      <ListItemText primary={`${props.item.name} (총 ${props.item.totalItemCount}개)`} />
      {expand ? <ExpandMore /> : <ExpandLess />}
    </ListItem>
    <Collapse in={expand} unmountOnExit>
      {(props.item.values.map((v => (
        <List component="div" disablePadding>
          <ListItem key={props.item.id + "-" + v.owner}>
            <ListItemText primary={`${v.owner} (${v.count ?? 1}개)`} />
          </ListItem>
        </List>
      ))))}
    </Collapse>

  </>
  );
}


export default App;
